import styled, { css } from 'styled-components';

import MaskedIcon from 'components/common/masked-icon';
import ResponsiveBox from 'components/common/responsive-box';
import { Heading4, Heading2 } from 'components/common/text';
import { desktop, mobile, tablet, desktopMinusOne } from 'utils/media';
import theme from 'utils/theme';

import { isOverlay } from './helpers';
import {
  WrapperProps,
  BannerProps,
  BannerAlignment,
  BannerContentWidth,
} from './types';

export const Banner = styled.div<BannerProps>`
  position: relative;
  background-position: center;
  background-size: cover;
  overflow: hidden;
  background-image: ${(props) =>
    props.bannerUrl ? `url(${props.bannerUrl})` : 'none'};
  flex: 50%;
  order: ${(props) => (props.alignment === 'left' ? 1 : 0)};
  ${(props) =>
    isOverlay(props.alignment)
      ? css`
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
        `
      : css`
          ${mobile} {
            position: relative;
            height: 0;
            padding-top: 64%;
          }
        `}
`;

export const TwoColWrapper = styled.div<WrapperProps>`
  position: relative;
  text-align: left;
  display: block;
  background-color: ${(props) =>
    props.backgroundColor && props.backgroundColor in theme.colors
      ? theme.colors[props.backgroundColor]
      : '#fff'};
  color: ${(props) =>
    props.textColor ? theme.colors[props.textColor] : 'inherit'};
  z-index: 1;

  ::before {
    display: block;
    content: '';
    width: 1;
    margin-left: -1;
    float: left;
    height: 0;
    padding-top: 85%;

    ${tablet} {
      padding-top: 33.333%;
    }
  }

  ::after {
    display: table;
    clear: both;
    content: '';
  }

  ${tablet} {
    display: flex;
  }
`;

interface TextSectionProps {
  alignment?: BannerAlignment;
  darkenOverlay?: boolean;
  contentWidth?: BannerContentWidth;
}

export const TextSection = styled.div<TextSectionProps>`
  display: flex;
  position: relative;
  flex: 50%;
  align-items: flex-end;
  padding: var(--site-gutter);
  z-index: 1;

  p {
    font-size: var(--step-1);
  }

  ${tablet} {
    align-items: center;
    justify-content: flex-start;
  }

  ${desktopMinusOne} {
    ${(props) =>
      props.alignment !== 'centerOverlay' && 'padding-right: var(--space-5xl)'};
    flex: ${(props) => props.contentWidth || '50%'};
  }

  ${(props) =>
    isOverlay(props.alignment) &&
    css`
      background: ${(props: TextSectionProps) =>
        props.darkenOverlay ? theme.colors.heroUnderlay : 'none'};

      ${(props: TextSectionProps) =>
        props.alignment === 'rightOverlay' &&
        css`
          ${tablet} {
            justify-content: flex-end;
          }
        `}

      > div {
        ${tablet} {
          width: 50%;
          min-width: 60ch;
          max-width: 100%;

          ${(props: TextSectionProps) =>
            props.alignment === 'centerOverlay' &&
            css`
              margin: 0 auto;
              text-align: center;
            `}
        }
      }
    `}
`;

export const ButtonWrapper = styled.div`
  margin-top: var(--space-xl);
`;

export const LabelWrapper = styled.div`
  margin-bottom: var(--space-l);

  p {
    font-size: var(--step-2);
  }
`;

export const StyledHeading4 = styled(Heading4)`
  line-height: 1;
`;

export const StyledMaskedIcon = styled(MaskedIcon)`
  margin-right: var(--space-3xs);
  display: inline-block;
`;

interface OverlayResponsiveBoxProps {
  textColor?: Sproutl.ColorScheme;
}

export const OverlayResponsiveBox = styled(
  ResponsiveBox,
)<OverlayResponsiveBoxProps>`
  display: flex;
  color: ${(props) =>
    props.textColor ? theme.colors[props.textColor] : 'inherit'};
`;

export const DesktopLargerHeading2 = styled(Heading2)`
  ${desktop} {
    font-size: var(--step-9);
  }
`;

export const PreLineContent = styled.div`
  white-space: pre-line;
  line-height: 1.4;
`;
